import { newError } from '#app/lib/error'
import { getIsbnData } from '#app/lib/isbn'
import { info } from '#app/lib/loggers'
import { treq } from '#app/lib/treq'
import { getTruthy, objectEntries } from '#app/lib/utils'
import type { EntityDraft } from '#app/types/entity'
import { isNonEmptyClaimValue } from '#entities/components/editor/lib/editors_helpers'
import { allowedValuesPerTypePerProperty } from '#entities/components/editor/lib/suggestions/property_values_shortlist'
import type { EntityAutocompleteSuggestion } from '#entities/components/entity_autocomplete_selector.svelte'
import { getOriginalLang } from '#entities/lib/get_original_lang'
import type { EntityType, PropertyUri, SimplifiedClaims, WdEntityUri } from '#server/types/entity'
import { getCurrentLang } from '#user/lib/i18n'
import { getEntityByUri, getPluralType, serializeEntity, type SerializedEntity } from './entities.ts'
import { propertiesEditorsConfigs } from './properties.ts'
import type { WikimediaLanguageCode } from 'wikibase-sdk'

async function getTitleFromWork ({ workLabels, workClaims, editionLang }: { workLabels: SerializedEntity['labels'], workClaims: SerializedEntity['claims'], editionLang: WikimediaLanguageCode }) {
  const inEditionLang = workLabels[editionLang]
  if (inEditionLang != null) return inEditionLang

  const inUserLang = workLabels[getCurrentLang()]
  if (inUserLang != null) return inUserLang

  const originalLang = await getOriginalLang(workClaims)
  const inWorkOriginalLang = workLabels[originalLang]
  if (inWorkOriginalLang != null) return inWorkOriginalLang

  const inEnglish = workLabels.en
  if (inEnglish != null) return inEnglish

  return Object.values(workLabels)[0]
}

export async function createWorkEditionDraft ({ workEntity, isbn }: { workEntity: SerializedEntity, isbn: string }) {
  const { labels: workLabels, claims: workClaims, uri: workUri, label } = workEntity

  const claims: SimplifiedClaims = {
    // instance of (P31) -> edition (Q3331189)
    'wdt:P31': [ 'wd:Q3331189' ],
    // edition or translation of (P629) -> created book
    'wdt:P629': [ workUri ],
  }

  let title, editionLang
  if (isbn) {
    const res = await getIsbnData(isbn)
    const { isbn13h, groupLang } = res
    const isbnDataTitle = getTruthy(res, 'title')
    const image = getTruthy(res, 'image')

    editionLang = groupLang

    if (isbnDataTitle) {
      title = isbnDataTitle
      info(title, 'title from isbn data')
    }

    // isbn 13 (isbn 10 - if it exist - will be added by the server)
    claims['wdt:P212'] = [ isbn13h ]

    if (image != null) {
      claims['invp:P2'] = [ image ]
    }
  }
  // if workEntity has been formatted already, use the label as title
  // known case: autocomplete editor suggestion (which do not have `labels` key)
  if (label) title = label
  if (!title) title = await getTitleFromWork({ workLabels, workClaims, editionLang })
  if (title == null) throw newError('no title could be found', workEntity)
  claims['wdt:P1476'] = [ title ]
  return { labels: {}, claims }
}

interface CreateByPropertyOptions {
  property: PropertyUri
  name: string
  relationSubjectEntity: EntityAutocompleteSuggestion
  createOnWikidata: EntityDraftWithCreationParams['createOnWikidata']
  lang?: WikimediaLanguageCode
}

export async function createByProperty (options: CreateByPropertyOptions) {
  let { property, name, relationSubjectEntity, createOnWikidata, lang } = options
  if (!lang) lang = getCurrentLang()

  const { entityType, entityTypeDefaultWdtP31: wdtP31 } = getPropertyDefaultSubjectEntityP31(property)
  if (wdtP31 == null) {
    throw newError('no entity creation function associated to this property', options)
  }

  const claims = { 'wdt:P31': [ wdtP31 ] }

  let labels: EntityDraftWithCreationParams['labels']
  if (property === 'wdt:P195') {
    labels = {}
    claims['wdt:P1476'] = [ name ]
  } else {
    labels = { [lang]: name }
  }

  if (property === 'wdt:P179') {
    claims['wdt:P50'] = relationSubjectEntity.claims['wdt:P50']
  }

  if (property === 'wdt:P195') {
    claims['wdt:P123'] = relationSubjectEntity.claims['wdt:P123']
    if (claims['wdt:P123'] == null) {
      throw newError('a publisher should be set before creating a collection', options)
    }
  }

  return createAndGetEntity({ type: entityType, labels, claims, createOnWikidata })
}

function getPropertyDefaultSubjectEntityP31 (property: PropertyUri) {
  const { entityValueTypes } = propertiesEditorsConfigs[property]
  const entityType = entityValueTypes[0] as EntityType
  const pluralizedEntityType = getPluralType(entityType)
  const entityTypeDefaultWdtP31 = allowedValuesPerTypePerProperty['wdt:P31'][pluralizedEntityType][0] as WdEntityUri
  return { entityType, entityTypeDefaultWdtP31 }
}

export async function createAndGetEntity (params: EntityDraftWithCreationParams) {
  const { claims } = params
  cleanupClaims(claims)
  return createEntity(params)
}

function cleanupClaims (claims: SimplifiedClaims) {
  for (const [ property, propertyClaims ] of objectEntries(claims)) {
    // @ts-expect-error
    claims[property] = propertyClaims.filter(isNonEmptyClaimValue)
    if (claims[property].length === 0) delete claims[property]
  }
}

export type EntityDraftWithCreationParams = Omit<EntityDraft, 'claimsEditors'> & { createOnWikidata?: boolean }

export async function createEntity (params: EntityDraftWithCreationParams) {
  const { labels, claims, createOnWikidata } = params
  const prefix = createOnWikidata === true ? 'wd' : 'inv'
  const { entity } = await treq('entities', 'post', 'default', { prefix, labels, claims })
  if (entity.type === 'edition') {
    // Make server/controllers/entities/lib/get_entities_by_isbns.ts enrich the newly created entity
    return getEntityByUri({ uri: entity.uri, refresh: true, autocreate: true })
  } else {
    return serializeEntity(entity)
  }
}
